import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  api,
  editionReadUrl,
  qs,
  type EditionManifest,
  type PageDetail,
  type PageHits,
  type Region,
  type SearchResponse,
} from '../lib/api'
import { AccessNotice, SeatNotice } from '../components/SeatNotice'
import { ReaderBarSlot } from '../components/ReaderLayout'
import { useSession } from '../lib/session'
import type { TypeKey } from '../components/Page'
import { Glyph } from '../components/viewer/Glyph'
import { PageWell, type WellHandle } from '../components/viewer/PageWell'
import { Toolbar, TabletBar } from '../components/viewer/Toolbar'
import { EditionRail } from '../components/viewer/EditionRail'
import { Inspector, type InspectorPopover } from '../components/viewer/Inspector'
import { ClipPanel } from '../components/viewer/ClipPanel'
import { StatusBar } from '../components/viewer/StatusBar'
import { MobileViewer, type SheetHeight } from '../components/viewer/MobileViewer'
import { KeyboardPanel } from '../components/viewer/KeyboardPanel'
import { RecordMissing } from '../components/viewer/RecordMissing'
import { useBreakpoint } from '../components/viewer/useBreakpoint'
import { useRegionDetail } from '../components/viewer/useRegionDetail'
import {
  TYPE_ORDER,
  buildRegions,
  countByType,
  deriveHits,
  geometryOf,
  parseRegionParam,
  permalinkPath,
  regionForHit,
} from '../components/viewer/model'

/**
 * The page viewer: a workspace, not a document.
 *
 * The page is the only thing at full opacity and the chrome overlays it or
 * gets out of the way. Everything else on the screen exists to answer one
 * question about the region the reader chose: where is it on the printed
 * sheet, how was its text obtained, and how sure is the machine about it.
 *
 * Boards 02 (desktop) and 04 (tablet and phone). The three arrangements are
 * genuinely different screens rather than one reflowed, because a broadsheet
 * is 792 points wide and a phone is 390.
 */

export function ViewerPage() {
  const { pageId = '' } = useParams()
  const { can, config } = useSession()
  const [params, setParams] = useSearchParams()
  const breakpoint = useBreakpoint()
  const navigate = useNavigate()

  const query = params.get('q') ?? ''
  const unitParam = params.get('unit')
  const highlight = params.get('highlight')
  const regionParam = params.get('r')

  // The page opens clean, as the publisher printed it: the client's
  // requirement. No region is drawn until the reader switches to Annotated
  // (or presses O); only the searched words are marked on the clean page.
  const [overlayOn, setOverlayOn] = useState(false)
  const [visibleTypes, setVisibleTypes] = useState<Set<TypeKey>>(() => new Set(TYPE_ORDER))
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [corrected, setCorrected] = useState(false)
  const [selecting, setSelecting] = useState(false)
  const [pendingClip, setPendingClip] = useState<Region | null>(null)
  const [savedClip, setSavedClip] = useState(false)
  const [popover, setPopover] = useState<InspectorPopover>(null)
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null)
  const [zoomPercent, setZoomPercent] = useState(0)
  const [hitIndex, setHitIndex] = useState(0)
  const [sheet, setSheet] = useState<SheetHeight>('handle')
  const [chromeHidden, setChromeHidden] = useState(false)
  const [draft, setDraft] = useState(query)
  const [keysOpen, setKeysOpen] = useState(false)

  const well = useRef<WellHandle | null>(null)
  const onWell = useCallback((handle: WellHandle) => {
    well.current = handle
  }, [])
  const onZoom = useCallback((percent: number) => setZoomPercent(Math.round(percent)), [])
  const onMarquee = useCallback((bbox: Region) => {
    setPendingClip(bbox)
    setSelecting(false)
  }, [])

  useEffect(() => setDraft(query), [query])

  // --- what the API has ------------------------------------------------------
  const page = useQuery({
    queryKey: ['page', pageId, 'words'],
    queryFn: () => api.get<PageDetail>(`/pages/${pageId}${qs({ words: true })}`),
    retry: false,
  })

  const edition = useQuery({
    queryKey: ['edition', page.data?.edition_id],
    queryFn: () => api.get<EditionManifest>(`/editions/${page.data!.edition_id}`),
    enabled: !!page.data?.edition_id,
  })

  /* The contract's own hit endpoint first. Where it has not landed the same
     boxes are computed from the page, so the navigator works either way. */
  const hitsQuery = useQuery({
    queryKey: ['page-hits', pageId, query],
    queryFn: () => api.get<PageHits>(`/pages/${pageId}/hits${qs({ q: query })}`),
    enabled: !!query,
    retry: false,
  })

  const editionHits = useQuery({
    queryKey: ['edition-hits', page.data?.edition_id, query],
    queryFn: () => api.get<SearchResponse>(`/search${qs({ q: query, group: 'page', limit: 60 })}`),
    enabled: !!query && !!page.data?.edition_id,
    retry: false,
  })

  /* The page's own edition block is the contract; until it lands the manifest
     supplies the same fields, so the geometry is never guessed. */
  const detail = useMemo<PageDetail | null>(() => {
    const data = page.data
    if (!data) return null
    if (data.edition) return data
    const manifest = edition.data
    if (!manifest) return data
    return {
      ...data,
      edition: {
        id: manifest.id,
        pub_date: manifest.pub_date,
        edition_label: manifest.edition_label,
        route: manifest.route ?? (data.source_type === 'digital' ? 'A' : 'B'),
        page_count: manifest.page_count,
        publication_name: manifest.publication_name,
        publication_slug: manifest.publication_slug,
        column_count: manifest.column_count,
        column_pitch_pt: manifest.column_pitch_pt,
        page_w_pt: manifest.page_w_pt ?? null,
        page_h_pt: manifest.page_h_pt ?? null,
      },
    }
  }, [page.data, edition.data])

  const geometry = useMemo(() => (detail ? geometryOf(detail) : null), [detail])
  const regions = useMemo(() => (detail ? buildRegions(detail) : []), [detail])
  const counts = useMemo(() => countByType(regions), [regions])

  const hits = useMemo(() => {
    if (!query || !detail) return []
    if (hitsQuery.data?.hits) return hitsQuery.data.hits
    return deriveHits(detail, query)
  }, [query, detail, hitsQuery.data])

  const hitsByPage = useMemo(() => {
    const map = new Map<string, number>()
    const data = editionHits.data
    if (!data) return map
    if (data.pages) {
      for (const group of data.pages) map.set(group.page_id, group.hit_count)
      return map
    }
    for (const result of data.results ?? []) {
      if (!result.page_id) continue
      map.set(result.page_id, (map.get(result.page_id) ?? 0) + 1)
    }
    return map
  }, [editionHits.data])

  const selected = useMemo(
    () => regions.find((region) => region.key === selectedKey) ?? null,
    [regions, selectedKey],
  )
  const record = useRegionDetail(selected)
  const selectionBox = useMemo(() => parseRegionParam(regionParam), [regionParam])

  // --- what a permalink or a search result asked for -------------------------
  useEffect(() => {
    if (!regions.length || selectedKey) return
    if (unitParam) {
      const match = regions.find((region) => region.unitId === unitParam)
      if (match) {
        setSelectedKey(match.key)
        well.current?.fitTo(match.bbox, 1.3)
        return
      }
    }
    if (highlight) {
      const match = regions.find(
        (region) => region.articleId === highlight || region.objectId === highlight,
      )
      if (match) {
        setSelectedKey(match.key)
        well.current?.fitTo(match.bbox, 1.3)
        return
      }
    }
    if (selectionBox) {
      const match = regions.find(
        (region) =>
          Math.abs(region.bbox[0] - selectionBox[0]) < 8 &&
          Math.abs(region.bbox[1] - selectionBox[1]) < 8,
      )
      if (match) setSelectedKey(match.key)
      well.current?.fitTo(selectionBox, 1.4)
    }
  }, [regions, selectedKey, unitParam, highlight, selectionBox])

  // --- the pages either side -------------------------------------------------
  const pages = useMemo(() => edition.data?.pages ?? [], [edition.data])
  const index = pages.findIndex((entry) => entry.id === pageId)
  const previous = index > 0 ? pages[index - 1] : null
  const next = index >= 0 && index < pages.length - 1 ? pages[index + 1] : null
  const search = params.toString() ? `?${params.toString()}` : ''

  const goTo = useCallback(
    (target: { id: string } | null) => {
      if (!target) return
      navigate(`/page/${target.id}${search}`)
    },
    [navigate, search],
  )

  const jumpPageId = useMemo(() => {
    const to = record?.jump?.to
    if (!to) return null
    return pages.find((entry) => entry.section_label === to)?.id ?? null
  }, [record, pages])

  // --- hits ------------------------------------------------------------------
  /* A word box is a few hundred pixels across, so fitting one fills the well
     with three letters. The navigator frames a couple of columns around the
     match instead, which is the width a reader reads at. */
  const goToHit = useCallback(
    (nextIndex: number) => {
      if (!hits.length || !geometry) return
      const wrapped = (nextIndex + hits.length) % hits.length
      setHitIndex(wrapped)
      const hit = hits[wrapped]
      const span = geometry.columnCount
        ? (geometry.widthPx / geometry.columnCount) * 2.2
        : geometry.widthPx * 0.45
      const cx = (hit.bbox[0] + hit.bbox[2]) / 2
      const cy = (hit.bbox[1] + hit.bbox[3]) / 2
      const width = Math.max(span, hit.bbox[2] - hit.bbox[0])
      const height = Math.max(width * 1.15, hit.bbox[3] - hit.bbox[1])
      well.current?.fitTo(
        [cx - width / 2, cy - height / 2, cx + width / 2, cy + height / 2],
        1,
      )
      const region = regionForHit(hit, regions)
      if (region) setSelectedKey(region.key)
    },
    [hits, regions, geometry],
  )

  useEffect(() => setHitIndex(0), [query, pageId])

  /* Opened from a search with no unit named: frame the first match at reading
     width rather than leaving the reader to find a box a few pixels wide on a
     page shown whole. Once per page and query, so the navigator's own moves
     are not undone. */
  const framed = useRef<string | null>(null)
  useEffect(() => {
    if (!query || !hits.length || !geometry) return
    if (unitParam || highlight || selectionBox) return
    const key = `${pageId}|${query}`
    if (framed.current === key) return
    framed.current = key
    goToHit(0)
  }, [query, hits, geometry, unitParam, highlight, selectionBox, pageId, goToHit])

  // --- the keyboard path -----------------------------------------------------
  const toggleType = useCallback((type: TypeKey) => {
    setVisibleTypes((current) => {
      const nextSet = new Set(current)
      if (nextSet.has(type)) nextSet.delete(type)
      else nextSet.add(type)
      return nextSet
    })
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const onRegionLayer = !!target?.hasAttribute?.('data-region-layer')

      switch (event.key) {
        case 'o':
        case 'O':
          event.preventDefault()
          setOverlayOn((value) => !value)
          break
        case 'ArrowLeft':
          if (onRegionLayer) return
          event.preventDefault()
          goTo(previous)
          break
        case 'ArrowRight':
          if (onRegionLayer) return
          event.preventDefault()
          goTo(next)
          break
        case 'n':
        case 'N':
          event.preventDefault()
          goToHit(hitIndex + 1)
          break
        case 'p':
        case 'P':
          event.preventDefault()
          goToHit(hitIndex - 1)
          break
        case 's':
        case 'S':
          event.preventDefault()
          setSelecting((value) => !value)
          break
        case 'c':
        case 'C':
          if (!selectedKey) return
          event.preventDefault()
          setPopover((value) => (value === 'cite' ? null : 'cite'))
          break
        case 'l':
        case 'L':
          if (!geometry) return
          event.preventDefault()
          void navigator.clipboard?.writeText(
            `${window.location.origin}${permalinkPath(geometry, selected?.bbox ?? null)}`,
          )
          setPopover((value) => (value === 'permalink' ? null : 'permalink'))
          break
        case 'r':
        case 'R':
          event.preventDefault()
          setCorrected((value) => !value)
          break
        case '+':
        case '=':
          event.preventDefault()
          well.current?.zoomBy(1.4)
          break
        case '-':
        case '_':
          event.preventDefault()
          well.current?.zoomBy(1 / 1.4)
          break
        case '0':
          event.preventDefault()
          well.current?.fit()
          break
        case '?':
          event.preventDefault()
          setKeysOpen((value) => !value)
          break
        default:
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [goTo, previous, next, goToHit, hitIndex, selectedKey, geometry, selected])

  // --- states ----------------------------------------------------------------
  if (page.isError) {
    return (
      <div className="p-4 md:p-6">
        <SeatNotice error={page.error} onRetry={() => void page.refetch()} />
        {/* The date asked for is known to the caller (a result card, browse) and travels in ?date=. */}
        <AccessNotice error={page.error} asked={params.get('date')} />
        <RecordMissing error={page.error} what="page" onRetry={() => void page.refetch()} />
      </div>
    )
  }
  if (!detail || !geometry) {
    return (
      <p className="p-6 text-[15px] text-ink2">
        Opening the page. The scan is delivered at its full raster, so the first view arrives before
        the detail does.
      </p>
    )
  }

  const productName = config?.product_name ?? ''
  const canClip = can('research.clipping')
  const compareHref = `/compare?left=${pageId}`
  // The whole issue as one document, opened at this page: browser PDF
  // viewers honour #page=N.
  const pdfHref = can('content.view_page_image')
    ? `${editionReadUrl(detail.edition_id)}#page=${detail.page_number}`
    : null

  const onSearch = (event: FormEvent) => {
    event.preventDefault()
    const nextParams = new URLSearchParams(params)
    if (draft.trim()) nextParams.set('q', draft.trim())
    else nextParams.delete('q')
    setParams(nextParams, { replace: true })
  }

  const searchInPage = (
    <form
      onSubmit={onSearch}
      className="flex h-[29px] w-full min-w-[150px] max-w-[400px] flex-1 items-center gap-2 rounded-[--radius-md] border border-rule2 bg-paper px-2.5"
      role="search"
    >
      <span className="text-ink3">
        <Glyph name="search" size={12} />
      </span>
      <input
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Search in this page"
        aria-label="Search in this page"
        className="min-w-0 flex-1 bg-transparent text-[13.5px] text-body outline-none placeholder:text-ink3"
      />
      <span className="whitespace-nowrap font-mono text-[10px] text-ink2">
        {query ? `${hits.length} hit${hits.length === 1 ? '' : 's'} here` : ''}
      </span>
    </form>
  )

  /* The reader bar already carries the section nav, the seat pill and the
     account controls. Below the desktop width there is no room left in it, so
     the breadcrumb and search-in-page move to the viewer's own control bar
     rather than overlapping the chrome that was there first. */
  const readerBar = (
    <div className="flex w-full min-w-0 items-center gap-4">
      <nav
        aria-label="Where this page sits"
        className="flex flex-none items-baseline gap-1.5 whitespace-nowrap text-[13.5px]"
      >
        <Link
          to={`/browse/${detail.edition_id}`}
          className="max-w-[200px] truncate"
          title={geometry.publication ?? undefined}
        >
          {geometry.publication ?? 'This publication'}
        </Link>
        <span className="text-rule2">/</span>
        <Link to={`/browse/${detail.edition_id}`} className="font-mono text-[12.5px]">
          {geometry.pubDate}
        </Link>
        <span className="text-rule2">/</span>
        <span className="font-mono text-[12.5px]">{geometry.label}</span>
      </nav>

      <div className="ml-auto flex min-w-0 flex-1 justify-end">{searchInPage}</div>
    </div>
  )

  const wellNode = (
    <PageWell
      pageId={detail.id}
      geometry={geometry}
      regions={regions}
      visibleTypes={visibleTypes}
      overlayOn={overlayOn}
      selectedKey={selectedKey}
      selectionBox={selectionBox}
      onSelect={(region) => {
        setSelectedKey(region?.key ?? null)
        setPopover(null)
      }}
      hits={hits}
      activeHit={hitIndex}
      onActivateHit={goToHit}
      selecting={selecting}
      onMarquee={onMarquee}
      pendingBox={pendingClip}
      onPointer={setPointer}
      onTransform={onZoom}
      onReady={onWell}
    />
  )

  const inspectorNode =
    pendingClip && canClip ? (
      <ClipPanel
        page={detail}
        geometry={geometry}
        bbox={pendingClip}
        region={selected}
        productName={productName}
        onCancel={() => setPendingClip(null)}
        onSaved={() => {
          setPendingClip(null)
          setSavedClip(true)
          window.setTimeout(() => setSavedClip(false), 2600)
        }}
      />
    ) : (
      <Inspector
        page={detail}
        geometry={geometry}
        region={selected}
        record={record}
        query={query}
        corrected={corrected}
        onCorrected={setCorrected}
        onClose={() => setSelectedKey(null)}
        onClip={() => selected && setPendingClip(selected.bbox)}
        canClip={canClip}
        productName={productName}
        dense={breakpoint === 'tablet'}
        regionCount={regions.length}
        popover={popover}
        onPopover={setPopover}
      />
    )

  if (breakpoint === 'mobile') {
    return (
      <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <MobileViewer
          pdfHref={pdfHref}
          page={detail}
          geometry={geometry}
          regions={regions}
          visibleTypes={visibleTypes}
          onToggleType={toggleType}
          counts={counts}
          overlayOn={overlayOn}
          onOverlay={setOverlayOn}
          selected={selected}
          record={record}
          onSelect={(region) => {
            setSelectedKey(region?.key ?? null)
            // The sheet never covers the region it describes: the page pans up
            // to make room as the sheet rises.
            if (region) well.current?.fitTo(region.bbox, 1.15)
          }}
          hits={hits}
          activeHit={hitIndex}
          onActivateHit={goToHit}
          query={query}
          corrected={corrected}
          onCorrected={setCorrected}
          onWell={onWell}
          onTransform={onZoom}
          zoomPercent={zoomPercent}
          selecting={selecting}
          onMarquee={onMarquee}
          pendingBox={pendingClip}
          onClip={() => selected && setPendingClip(selected.bbox)}
          canClip={canClip}
          sheet={sheet}
          onSheet={setSheet}
          chromeHidden={chromeHidden}
          onChrome={setChromeHidden}
          selectionBox={selectionBox}
          clipPanel={
            pendingClip && canClip ? (
              <ClipPanel
                page={detail}
                geometry={geometry}
                bbox={pendingClip}
                region={selected}
                productName={productName}
                onCancel={() => setPendingClip(null)}
                onSaved={() => setPendingClip(null)}
              />
            ) : null
          }
        />
      </div>
    )
  }

  if (breakpoint === 'tablet') {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1">
          {wellNode}
          <aside className="flex w-[300px] flex-none flex-col overflow-hidden">{inspectorNode}</aside>
        </div>
        <TabletBar
          pageLabelText={geometry.label}
          pageCount={geometry.pageCount}
          onPrevPage={() => goTo(previous)}
          onNextPage={() => goTo(next)}
          hasPrev={!!previous}
          hasNext={!!next}
          overlayOn={overlayOn}
          onOverlay={setOverlayOn}
          counts={counts}
          visibleTypes={visibleTypes}
          onToggleType={toggleType}
          zoomPercent={zoomPercent}
          onZoomIn={() => well.current?.zoomBy(1.4)}
          onZoomOut={() => well.current?.zoomBy(1 / 1.4)}
          search={searchInPage}
        />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <ReaderBarSlot>{readerBar}</ReaderBarSlot>

      <Toolbar
        pageLabelText={geometry.label}
        pageCount={geometry.pageCount}
        onPrevPage={() => goTo(previous)}
        onNextPage={() => goTo(next)}
        hasPrev={!!previous}
        hasNext={!!next}
        hitIndex={hitIndex}
        hitTotal={hits.length}
        onPrevHit={() => goToHit(hitIndex - 1)}
        onNextHit={() => goToHit(hitIndex + 1)}
        zoomPercent={zoomPercent}
        onZoomIn={() => well.current?.zoomBy(1.4)}
        onZoomOut={() => well.current?.zoomBy(1 / 1.4)}
        onFit={() => well.current?.fit()}
        overlayOn={overlayOn}
        onOverlay={setOverlayOn}
        counts={counts}
        visibleTypes={visibleTypes}
        onToggleType={toggleType}
        selecting={selecting}
        onSelectRegion={() => setSelecting((value) => !value)}
        compareHref={compareHref}
        pdfHref={pdfHref}
      />

      <div className="flex min-h-0 flex-1">
        <EditionRail
          pages={pages}
          currentPageId={pageId}
          editionId={detail.edition_id}
          pageCount={geometry.pageCount ?? pages.length}
          hitsByPage={hitsByPage}
          jumpPageId={jumpPageId}
        />
        {wellNode}
        <aside className="relative flex w-[432px] flex-none flex-col overflow-hidden">
          {inspectorNode}
          {savedClip && (
            <div className="absolute inset-x-4 bottom-4 rounded-[--radius-md] px-3 py-2 text-[13.5px]" style={{ background: 'var(--jl-accent-100)', boxShadow: 'inset 3px 0 0 var(--jl-accent)' }}>
              Clipping saved with its citation frozen. <Link to="/clippings">Open clippings</Link>
            </div>
          )}
        </aside>
      </div>

      <StatusBar
        geometry={geometry}
        pointer={pointer}
        regionCount={regions.filter((region) => visibleTypes.has(region.typeKey)).length}
        selectedCount={selected ? 1 : 0}
        onKeys={() => setKeysOpen(true)}
      />

      {keysOpen && <KeyboardPanel onClose={() => setKeysOpen(false)} />}
    </div>
  )
}
